/**
 * « Préparer l'année » (lot U5, ADR-0016) : le logiciel PROPOSE les interventions de l'année, on décide.
 *
 * Deux parties :
 * - les ENTRETIENS d'après les gammes et les heures (2026-10-30) : TOUT le parc de chaque site, une ligne par machine (2026-10-31),
 *   machines sans gamme comprises (visite sans palier) ; rien n'est coché, c'est l'utilisateur qui choisit les machines ;
 * - les ÉCHÉANCES RÉGLEMENTAIRES de l'horizon qui n'ont pas encore d'intervention.
 * Cocher puis « Créer » fait naître une intervention « à affecter » par ligne (fenêtre, ni jour ni technicien ; pour un entretien,
 * le palier à faire, dont les pièces se lisent dans la gamme) : le planning la prend ensuite comme toute autre. Rien n'est stocké
 * avant ce geste, et rejouer le geste ne crée jamais deux interventions pour la même ligne. L'écran dit ce qu'il ne couvre pas.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useDroitsPlanning } from '../auth/useDroits';
import { ApiError, api, type BilanPreparationAnnee, type NatureBon, type PreparationAnnee, type Site } from '../api/client';
import { PreparerSite } from '../composants/PreparerSite';
import { ErreurFormulaire } from '../composants/Formulaire';
import {
  FILTRES_ANNEE,
  HORIZONS_RAPIDES,
  filtrer,
  groupesParMois,
  peutCreer,
  phraseBilan,
  phraseConfirmation,
  phraseFenetre,
  phraseRaison,
  phraseRetard,
  type FiltreAnnee,
} from '../domaine/preparer-annee';
import {
  basculerMachine,
  basculerSite,
  cochables,
  cochablesMachine,
  etatCaseMachine,
  groupesParMachine,
  corpsCreationAnnee,
  filtrerEntretiens,
  groupesParSite,
  nomMachine,
  phrasePalier,
  phraseQuand,
  phraseRetardEntretien,
} from '../domaine/preparer-annee-entretiens';
import { jourFr } from '../domaine/planning';

type Partie = 'ENTRETIENS' | 'REGLEMENTAIRE';

export function PreparerAnneePage() {
  const droits = useDroitsPlanning();
  const [params, setParams] = useSearchParams();
  const siteChoisi = params.get('site') ?? '';
  const [sitesListe, setSitesListe] = useState<readonly Site[]>([]);
  const [mois, setMois] = useState(12);
  const [revue, setRevue] = useState<PreparationAnnee | null>(null);
  const [erreur, setErreur] = useState<Error | null>(null);
  const [natures, setNatures] = useState<readonly NatureBon[]>([]);
  const [natureId, setNatureId] = useState('');
  const [partie, setPartie] = useState<Partie>('ENTRETIENS');
  // Tout le parc est montré d'emblée (Benjamin, 2026-10-31) : on choisit soi-même ce qu'on met dans la visite.
  const [filtre, setFiltre] = useState<FiltreAnnee>('TOUTES');
  const [recherche, setRecherche] = useState('');
  const [cochees, setCochees] = useState<ReadonlySet<string>>(new Set());
  const [entretiensCoches, setEntretiensCoches] = useState<ReadonlySet<string>>(new Set());
  const [enCours, setEnCours] = useState(false);
  const [bilan, setBilan] = useState<BilanPreparationAnnee | null>(null);

  const charger = useCallback(async (horizon: number) => {
    try {
      const r = await api.preparerAnnee(horizon);
      setRevue(r);
      // Rien n'est coché au chargement (Benjamin, 2026-10-31) : c'est lui qui choisit, site par site ou ligne par ligne.
      setCochees(new Set());
      setEntretiensCoches(new Set());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Revue illisible.'));
    }
  }, []);

  useEffect(() => {
    if (siteChoisi === '') void charger(mois);
  }, [charger, mois, siteChoisi]);

  useEffect(() => {
    let actif = true;
    api
      .sites()
      .then((l) => {
        if (actif) setSitesListe([...l].sort((a, b) => `${a.customerName} ${a.name}`.localeCompare(`${b.customerName} ${b.name}`, 'fr')));
      })
      .catch(() => undefined); // sans la liste, le choix du site reste vide et la vue d'ensemble reste disponible
    return () => {
      actif = false;
    };
  }, []);

  const choisirSite = (id: string) => {
    const suite = new URLSearchParams(params);
    if (id === '') suite.delete('site');
    else suite.set('site', id);
    setParams(suite);
  };

  useEffect(() => {
    let actif = true;
    api
      .naturesBon()
      .then((n) => {
        if (actif) setNatures(n);
      })
      .catch(() => undefined); // sans la liste, le choix de la nature reste vide et la création est refusée par l'écran
    return () => {
      actif = false;
    };
  }, []);

  const entretiens = useMemo(() => revue?.entretiens ?? [], [revue]);
  const visibles = useMemo(() => (revue ? filtrer(revue.propositions, filtre, recherche) : []), [revue, filtre, recherche]);
  const groupes = useMemo(() => (revue ? groupesParMois(visibles, revue.aujourdhui) : []), [revue, visibles]);
  const sites = useMemo(() => groupesParSite(filtrerEntretiens(entretiens, filtre, recherche)), [entretiens, filtre, recherche]);
  const nature = natures.find((n) => n.id === natureId) ?? null;
  const total = cochees.size + entretiensCoches.size;
  const lecture = peutCreer(natureId, new Set([...cochees, ...entretiensCoches]));

  const bascule = (id: string) =>
    setCochees((c) => {
      const suite = new Set(c);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });
  const basculeEntretien = (id: string) =>
    setEntretiensCoches((c) => {
      const suite = new Set(c);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });

  const creer = async () => {
    if (!droits.peutGererPlanning || !lecture.ok || !nature) return;
    if (!window.confirm(phraseConfirmation(total, nature.label))) return;
    setEnCours(true);
    try {
      const r = await api.creerDepuisEcheances(corpsCreationAnnee(natureId, cochees, entretiensCoches));
      // Relire AVANT d'annoncer : la relecture remet l'erreur à zéro, le bilan se pose ensuite.
      await charger(mois);
      setBilan(r);
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Création refusée.'));
    } finally {
      setEnCours(false);
    }
  };

  const compteEntretiens = revue?.compteEntretiens ?? { aCreer: 0, dejaPlanifiees: 0, aCompleter: 0 };
  const nombres: Record<FiltreAnnee, number> =
    partie === 'ENTRETIENS'
      ? { A_CREER: compteEntretiens.aCreer, DEJA_PLANIFIEE: compteEntretiens.dejaPlanifiees, A_COMPLETER: compteEntretiens.aCompleter, TOUTES: entretiens.length }
      : {
          A_CREER: revue?.compte.aCreer ?? 0,
          DEJA_PLANIFIEE: revue?.compte.dejaPlanifiees ?? 0,
          A_COMPLETER: revue?.compte.aCompleter ?? 0,
          TOUTES: revue?.propositions.length ?? 0,
        };

  /** Le libellé d'une ligne refusée au bilan : l'échéance ou l'entretien qu'elle était. */
  const libelleRefus = (id: string): string => {
    const p = revue?.propositions.find((x) => x.obligationId === id);
    if (p) return `${p.regle} — ${p.machine.libelle ?? p.machine.modele ?? 'machine'} : `;
    const e = entretiens.find((x) => x.id === id);
    return e ? `${nomMachine(e)} — palier ${e.palier?.code ?? '?'} : ` : '';
  };

  return (
    <div className="gammes-page preparer-annee">
      <div className="entete-page">
        <div>
          <h1>Préparer l’année</h1>
          <p>
            Les entretiens d’après les gammes et les heures, et les échéances réglementaires à venir. Cochez les machines, créez : chaque ligne devient une
            intervention « à affecter », que vous planifiez ensuite.
          </p>
        </div>
        <div className="saisie-ligne">
          <Link className="bouton-lien secondaire-lien" to="/planning/interventions">
            Retour au planning
          </Link>
        </div>
      </div>

      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />

      <div className="saisie-ligne" role="group" aria-label="Site">
        <label>
          Site
          <select value={siteChoisi} onChange={(e) => choisirSite(e.target.value)}>
            <option value="">Tous les sites (vue d’ensemble)</option>
            {sitesListe.map((x) => (
              <option key={x.id} value={x.id}>
                {x.customerName} · {x.name}
                {x.city ? ` (${x.city})` : ''}
              </option>
            ))}
          </select>
        </label>
        <span className="aide">Un site : son contrat d’entretien, ses visites dues et ses visites de contrôle.</span>
      </div>

      {siteChoisi !== '' ? (
        <PreparerSite key={siteChoisi} siteId={siteChoisi} natures={natures} />
      ) : (
        <>
      <div className="saisie-ligne" role="group" aria-label="Horizon">
        <span className="aide">Horizon :</span>
        {HORIZONS_RAPIDES.map((m) => (
          <button key={m} type="button" className={`chip${m === mois ? ' actif' : ''}`} aria-pressed={m === mois} disabled={enCours} onClick={() => setMois(m)}>
            {m} mois
          </button>
        ))}
        {revue && <span className="aide">jusqu’au {jourFr(revue.au)}</span>}
      </div>

      {revue && (
        <details className="aide">
          <summary>Ce que cette revue ne propose pas encore</summary>
          <ul>
            {revue.nonCouvert.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </details>
      )}

      {bilan && (
        <div className="message" role="status">
          <strong>{phraseBilan(bilan)}</strong>
          {bilan.creees.length > 0 && (
            <>
              {' '}
              <Link to="/planning/interventions">Les voir dans le planning (liste « À affecter »)</Link>
            </>
          )}
          {bilan.refusees.length > 0 && (
            <ul>
              {bilan.refusees.map((r) => (
                <li key={r.obligationId}>
                  {libelleRefus(r.obligationId)}
                  {r.raison}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!revue && !erreur && <div className="vide">Calcul des propositions…</div>}

      {revue && (
        <>
          <div className="chips-secondaires preparer-parties" role="group" aria-label="Partie">
            <button type="button" className={`chip${partie === 'ENTRETIENS' ? ' actif' : ''}`} aria-pressed={partie === 'ENTRETIENS'} onClick={() => setPartie('ENTRETIENS')}>
              Entretiens <span className="aide">{compteEntretiens.aCreer} à créer</span>
            </button>
            <button type="button" className={`chip${partie === 'REGLEMENTAIRE' ? ' actif' : ''}`} aria-pressed={partie === 'REGLEMENTAIRE'} onClick={() => setPartie('REGLEMENTAIRE')}>
              Réglementaire <span className="aide">{revue.compte.aCreer} à créer</span>
            </button>
          </div>

          <div className="maj-gammes-outils">
            <div className="chips-secondaires" role="group" aria-label="Filtrer les lignes">
              {FILTRES_ANNEE.map((f) => (
                <button key={f.valeur} type="button" className={`chip${f.valeur === filtre ? ' actif' : ''}`} aria-pressed={f.valeur === filtre} onClick={() => setFiltre(f.valeur)}>
                  {f.libelle} <span className="aide">{nombres[f.valeur]}</span>
                </button>
              ))}
            </div>
            <input type="text" aria-label="Chercher" placeholder="Chercher : client, site, machine, famille…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          </div>

          {partie === 'ENTRETIENS' &&
            (sites.length === 0 ? (
              <div className="vide">Aucun entretien ne correspond à ce filtre{recherche.trim() ? ` et à « ${recherche.trim()} »` : ''}.</div>
            ) : (
              <>
                <p className="aide">
                  Tout le parc de chaque site, une ligne par machine. Rien n’est coché : cochez les machines à mettre dans la visite (la case de la machine coche toutes ses visites de l’horizon ; décochez-en une si besoin).
                </p>
                {sites.map((g) => {
                  const ids = cochables(g);
                  const tout = ids.length > 0 && ids.every((id) => entretiensCoches.has(id));
                  return (
                    <section key={g.cle}>
                      <h2 className="preparer-site">
                        {g.libelle} <span className="aide">{groupesParMachine(g.lignes).length} machines</span>
                        {g.cle !== 'sans-site' && (
                          <button type="button" className="secondaire discret" onClick={() => choisirSite(g.cle)}>
                            Préparer ce site
                          </button>
                        )}
                        {ids.length > 0 && droits.peutGererPlanning && (
                          <button
                            type="button"
                            className="secondaire discret"
                            disabled={enCours}
                            onClick={() => setEntretiensCoches((c) => basculerSite(c, g))}
                          >
                            {tout ? 'Tout décocher' : 'Tout cocher'}
                          </button>
                        )}
                      </h2>
                      <div className="tableau-defilant">
                        <table className="tableau-ecarts">
                          <thead>
                            <tr>
                              <th>Mettre</th>
                              <th>Machine</th>
                              <th>Visites de l’horizon</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupesParMachine(g.lignes).map((m) => {
                              const ids = cochablesMachine(m);
                              const etat = etatCaseMachine(entretiensCoches, m);
                              return (
                                <tr key={m.id}>
                                  <td>
                                    {ids.length > 0 && (
                                      <input
                                        type="checkbox"
                                        aria-label={`Mettre ${m.nom} dans les visites`}
                                        checked={etat === 'TOUTES'}
                                        ref={(el) => {
                                          if (el) el.indeterminate = etat === 'PARTIEL';
                                        }}
                                        disabled={enCours || !droits.peutGererPlanning}
                                        onChange={() => setEntretiensCoches((c) => basculerMachine(c, m))}
                                      />
                                    )}
                                  </td>
                                  <td>
                                    <Link to={`/parc?asset=${m.id}`}>{m.nom}</Link>
                                    <div className="aide">
                                      {[m.famille, m.lignes[0]?.machine.designationClient, m.lignes[0]?.machine.numeroSerie ? `n° ${m.lignes[0].machine.numeroSerie}` : null]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </div>
                                    {m.sansGamme && <div className="aide">Pas de gamme d’entretien : visite sans palier.</div>}
                                  </td>
                                  <td>
                                    {m.lignes.map((l) => (
                                      <div key={l.id} className="saisie-ligne">
                                        {l.etat === 'A_CREER' && m.lignes.length > 1 && (
                                          <input
                                            type="checkbox"
                                            aria-label={`Créer la visite ${phrasePalier(l)} de ${m.nom}`}
                                            checked={entretiensCoches.has(l.id)}
                                            disabled={enCours || !droits.peutGererPlanning}
                                            onChange={() => basculeEntretien(l.id)}
                                          />
                                        )}
                                        <span>
                                          {!m.sansGamme && <strong>{phrasePalier(l)}</strong>} {phraseQuand(l)}
                                          {l.mode && <span className="aide"> · {l.mode === 'CALENDAR' ? 'au calendrier' : 'au compteur'}</span>}
                                          {phraseRetardEntretien(l) && <span className="aide"> · {phraseRetardEntretien(l)}</span>}
                                          {l.etat === 'A_CREER' && l.message && !m.sansGamme && <span className="aide"> · {l.message}</span>}
                                          {l.etat === 'DEJA_PLANIFIEE' && l.intervention && (
                                            <>
                                              {' · '}
                                              <Link to={`/planning/interventions?intervention=${l.intervention.id}`}>
                                                Déjà planifié{l.intervention.numero ? ` : ${l.intervention.numero}` : ''}
                                              </Link>
                                            </>
                                          )}
                                          {l.etat === 'A_COMPLETER' && <span className="message attention"> {l.message ?? 'À compléter.'}</span>}
                                        </span>
                                      </div>
                                    ))}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  );
                })}
              </>
            ))}

          {partie === 'REGLEMENTAIRE' &&
            (groupes.length === 0 ? (
              <div className="vide">Aucune échéance ne correspond à ce filtre{recherche.trim() ? ` et à « ${recherche.trim()} »` : ''}.</div>
            ) : (
              groupes.map((g) => (
                <section key={g.cle}>
                  <h2>
                    {g.libelle} <span className="aide">{g.propositions.length}</span>
                  </h2>
                  <div className="tableau-defilant">
                    <table className="tableau-ecarts">
                      <thead>
                        <tr>
                          <th>Créer</th>
                          <th>Échéance</th>
                          <th>Règle</th>
                          <th>Machine</th>
                          <th>Site / client</th>
                          <th>État</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.propositions.map((p) => (
                          <tr key={p.obligationId}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Créer une intervention pour ${p.regle} — ${p.machine.libelle ?? p.machine.modele ?? 'machine'}`}
                                checked={cochees.has(p.obligationId)}
                                disabled={p.etat !== 'A_CREER' || enCours || !droits.peutGererPlanning}
                                onChange={() => bascule(p.obligationId)}
                              />
                            </td>
                            <td>
                              {jourFr(p.echeance)}
                              <div className="aide">{phraseRetard(p.retardJours)}</div>
                            </td>
                            <td>
                              {p.regle}
                              <div className="aide">{p.jalon}</div>
                              {p.organisme && <div className="aide">Organisme : {p.organisme}</div>}
                            </td>
                            <td>
                              <Link to={`/parc?asset=${p.machine.id}`}>{p.machine.libelle ?? p.machine.modele ?? 'Machine'}</Link>
                              <div className="aide">{[p.machine.designationClient, p.machine.modele, p.machine.numeroSerie ? `n° ${p.machine.numeroSerie}` : null].filter(Boolean).join(' · ')}</div>
                            </td>
                            <td>
                              {p.site ? `${p.site.nom}${p.site.ville ? ` (${p.site.ville})` : ''}` : '—'}
                              {p.client && <div className="aide">{p.client}</div>}
                            </td>
                            <td>
                              {p.etat === 'A_CREER' && <span className="aide">Fenêtre {phraseFenetre(p.fenetre.debut, p.fenetre.fin)}</span>}
                              {p.etat === 'DEJA_PLANIFIEE' && p.intervention && (
                                <Link to={`/planning/interventions?intervention=${p.intervention.id}`}>
                                  Déjà planifiée{p.intervention.numero ? ` : ${p.intervention.numero}` : ''}
                                </Link>
                              )}
                              {p.etat === 'A_COMPLETER' && <span className="message attention">{phraseRaison(p.raison)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            ))}

          <div className="editeur-barre">
            <label>
              Nature des interventions à créer
              <select value={natureId} disabled={enCours || !droits.peutGererPlanning} onChange={(e) => setNatureId(e.target.value)}>
                <option value="">Choisir…</option>
                {natures.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="editeur-barre-actions">
              <span className="aide">
                {!droits.peutGererPlanning
                  ? 'Lecture seule : créer des interventions demande le droit de gérer le planning.'
                  : total === 0
                    ? 'Aucune ligne cochée'
                    : `${entretiensCoches.size} ${entretiensCoches.size > 1 ? 'entretiens cochés' : 'entretien coché'}, ${cochees.size} ${cochees.size > 1 ? 'échéances cochées' : 'échéance cochée'}`}
              </span>
              <div className="saisie-ligne">
                <button
                  type="button"
                  className="secondaire"
                  disabled={enCours || total === 0}
                  onClick={() => {
                    setCochees(new Set());
                    setEntretiensCoches(new Set());
                  }}
                >
                  Tout décocher
                </button>
                <button type="button" disabled={enCours || !droits.peutGererPlanning || !lecture.ok} title={lecture.ok ? undefined : lecture.erreur} onClick={() => void creer()}>
                  Créer {total === 1 ? 'l’intervention' : `les ${total} interventions`}
                </button>
              </div>
            </div>
            {droits.peutGererPlanning && !lecture.ok && <div className="aide">{lecture.erreur}</div>}
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}
